import * as React from 'react'
import { useState } from 'react'
import { checkWord } from '../../utils/wordHelper'
import { FLIP_DELAY } from '../Wordle'
import KeyboardAction from './KeyboardAction'
import KeyboardLetter from './KeyboardLetter'

type Props = {
  guesses: string[]
  guessCount: number
  word: string
  onSubmitWord: (guess: string) => void
  onClickLetter: (letter: string) => void
  onClearLetter: () => void
}

const Keyboard = ({
  guesses,
  guessCount,
  word,
  onClickLetter,
  onClearLetter,
  onSubmitWord,
}: Props) => {
  const topRow = ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P']
  const middleRow = ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L']
  const bottomRow = ['Z', 'X', 'C', 'V', 'B', 'N', 'M']

  const [correctLetters, setCorrectLetters] = useState<Set<string>>(new Set())
  const [presentLetters, setPresentLetters] = useState<Set<string>>(new Set())
  const [wrongLetters, setWrongLetters] = useState<Set<string>>(new Set())

  const handleKeyboardEvent = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const { key } = event

    if (key === 'Backspace') onClearLetter()
    else if (key === 'Enter') handleSubmitWord()
    else if (key.length === 1) onClickLetter(key.toUpperCase())
  }

  const handleSubmitWord = () => {
    const guess = guesses[guessCount]

    if (guess.length < word.length) return

    if (!checkWord(guess.toLowerCase())) {
      alert(`${guess} is not a valid word!`)
      return
    }

    onSubmitWord(guess)

    setTimeout(() => {
      const guessArray = Array.from(guesses[guessCount])

      setPresentLetters(
        new Set([
          ...presentLetters,
          ...guessArray.filter((letter, i) => word.includes(letter) && word[i] !== letter),
        ])
      )
      setCorrectLetters(
        new Set([...correctLetters, ...guessArray.filter((letter, i) => word[i] === letter)])
      )
      setWrongLetters(
        new Set([...wrongLetters, ...guessArray.filter((letter) => !word.includes(letter))])
      )
    }, word.length * FLIP_DELAY)
  }

  return (
    <div
      className="flex flex-col justify-center mt-8 focus:outline-none"
      onKeyDown={handleKeyboardEvent}
      tabIndex={0}
    >
      <div className="flex flex-row justify-center mt-4">
        {topRow.map((letter) => (
          <KeyboardLetter
            key={letter}
            letter={letter}
            onClickLetter={onClickLetter}
            correctLetters={correctLetters}
            presentLetters={presentLetters}
            wrongLetters={wrongLetters}
          />
        ))}
      </div>
      <div className="flex flex-row justify-center mt-4">
        {middleRow.map((letter) => (
          <KeyboardLetter
            key={letter}
            letter={letter}
            onClickLetter={onClickLetter}
            correctLetters={correctLetters}
            presentLetters={presentLetters}
            wrongLetters={wrongLetters}
          />
        ))}
      </div>
      <div className="flex flex-row justify-center mt-4">
        <KeyboardAction action="submit" onClickAction={handleSubmitWord} />

        {bottomRow.map((letter) => (
          <KeyboardLetter
            key={letter}
            letter={letter}
            onClickLetter={onClickLetter}
            correctLetters={correctLetters}
            presentLetters={presentLetters}
            wrongLetters={wrongLetters}
          />
        ))}

        <KeyboardAction action="clear" onClickAction={onClearLetter} />
      </div>
    </div>
  )
}

export default Keyboard
